import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Grid_Lines() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🟦 CSS Grid Lines: Complete Guide</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS Grid lines allow you to precisely place grid items within a grid
        container. You can use explicit line numbers, span multiple lines, use
        negative indexing, or name lines for clarity.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>grid-column-start</code> / <code>grid-column-end</code> for
            columns.
          </li>
          <li>
            <code>grid-row-start</code> / <code>grid-row-end</code> for rows.
          </li>
          <li>
            <code>grid-column</code> and <code>grid-row</code> shorthand.
          </li>
          <li>
            Use <code>span n</code> to span multiple lines.
          </li>
          <li>Negative line numbers count from the end.</li>
          <li>Named lines allow semantic placement.</li>
        </ul>
      </div>

      {/* Example 1: Basic line placement */}
      <h2 className="section-title">1️⃣ Basic Grid Line Placement</h2>
      <CodeCopyBlock
        code={`.grid-basic {
  display: grid;
  grid-template-columns: 100px 100px 100px;
  grid-template-rows: 100px 100px;
  gap: 10px;
}
.item1 { grid-column-start: 1; grid-column-end: 3; grid-row-start: 1; grid-row-end: 2; }
.item2 { grid-column: 3 / 4; grid-row: 1 / 3; }`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "100px 100px 100px",
          gridTemplateRows: "100px 100px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            background: "#3b82f6",
            color: "white",
            gridColumn: "1 / 3",
            gridRow: "1 / 2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Item 1
        </div>
        <div
          style={{
            background: "#10b981",
            color: "white",
            gridColumn: "3 / 4",
            gridRow: "1 / 3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Item 2
        </div>
        <div
          style={{
            background: "#f97316",
            color: "white",
            gridColumn: "1 / 2",
            gridRow: "2 / 3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Item 3
        </div>
        <div
          style={{
            background: "#fbbf24",
            color: "white",
            gridColumn: "2 / 3",
            gridRow: "2 / 3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Item 4
        </div>
      </div>

      {/* Example 2: Span multiple lines */}
      <h2 className="section-title">2️⃣ Span Multiple Lines</h2>
      <CodeCopyBlock
        code={`.grid-span {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: 100px 100px;
  gap: 10px;
}
.item-span { grid-column: span 2; grid-row: span 2; }`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gridTemplateRows: "100px 100px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            background: "#3b82f6",
            color: "white",
            gridColumn: "span 2",
            gridRow: "span 2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Span 2x2
        </div>
        <div
          style={{
            background: "#10b981",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Box 2
        </div>
        <div
          style={{
            background: "#f97316",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Box 3
        </div>
      </div>

      {/* Example 3: Negative lines */}
      <h2 className="section-title">3️⃣ Negative Line Numbers</h2>
      <CodeCopyBlock
        code={`.grid-negative {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 100px 100px;
  gap: 10px;
}
.item-neg { grid-column: -3 / -1; grid-row: -2 / -1; }`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,1fr)",
          gridTemplateRows: "100px 100px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            background: "#3b82f6",
            color: "white",
            gridColumn: "-3 / -1",
            gridRow: "-2 / -1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Negative Line
        </div>
        <div
          style={{
            background: "#10b981",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Box 2
        </div>
        <div
          style={{
            background: "#f97316",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Box 3
        </div>
      </div>

      {/* Example 4: Named lines */}
      <h2 className="section-title">4️⃣ Named Grid Lines</h2>
      <CodeCopyBlock
        code={`.grid-named {
  display: grid;
  grid-template-columns: [start] 100px [center] 100px [end];
  grid-template-rows: [top] 100px [bottom];
}
.item-named { grid-column: start / center; grid-row: top / bottom; }`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "100px 100px",
          gridTemplateRows: "100px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            background: "#3b82f6",
            color: "white",
            gridColumn: "1 / 2",
            gridRow: "1 / 2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Named Line
        </div>
        <div
          style={{
            background: "#10b981",
            color: "white",
            gridColumn: "2 / 3",
            gridRow: "1 / 2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Box 2
        </div>
      </div>

      {/* Example 5: Shorthand grid-column / grid-row */}
      <h2 className="section-title">5️⃣ Shorthand Placement</h2>
      <CodeCopyBlock
        code={`.grid-shorthand {
  display: grid;
  grid-template-columns: repeat(3, 100px);
  grid-template-rows: repeat(2, 100px);
  gap: 10px;
}
.item-shorthand { grid-column: 2 / 4; grid-row: 1 / 3; }`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,100px)",
          gridTemplateRows: "repeat(2,100px)",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            background: "#f97316",
            color: "white",
            gridColumn: "2 / 4",
            gridRow: "1 / 3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Shorthand
        </div>
        <div
          style={{
            background: "#10b981",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Box 2
        </div>
        <div
          style={{
            background: "#3b82f6",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          Box 3
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fcd34d)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Combine line numbers and spans for complex item placement.</li>
          <li>Use negative indexing with named lines for precise layouts.</li>
          <li>
            Experiment with different combinations to create advanced
            dashboards.
          </li>
          <li>Mix Grid lines with Grid Areas for semantic placement.</li>
        </ul>
      </div>
    </div>
  );
}
